import React, { useState } from "react";
import {
  Alert,
  Button,
  Descriptions,
  Form,
  InputNumber,
  message,
  Modal,
  Select,
  Tag,
} from "antd";
import { SwapOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { convertCreditCurrency } from "./creditsService";
import { Credit } from "./types";

const CURRENCIES = ["EUR", "BAM", "RSD", "USD", "CHF", "GBP"];

type Props = {
  open: boolean;
  credit: Credit | null;
  token?: string;
  onCancel: () => void;
  onSuccess: (credit: Credit) => void;
};

const CreditCurrencyConvertModal: React.FC<Props> = ({
  open,
  credit,
  token,
  onCancel,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const targetCurrency = Form.useWatch("targetCurrency", form);
  const exchangeRate = Form.useWatch("exchangeRate", form);

  const sourceCurrency = credit?.currency || "EUR";

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  const handleSubmit = async (values: { targetCurrency: string; exchangeRate: number }) => {
    if (!credit) return;
    setLoading(true);
    try {
      const updated = await convertCreditCurrency(
        credit._id,
        values.targetCurrency,
        values.exchangeRate,
        token,
      );
      message.success(
        t("Credit converted from {{from}} to {{to}}", {
          from: sourceCurrency,
          to: values.targetCurrency,
        }),
      );
      form.resetFields();
      onSuccess(updated);
    } catch (err: any) {
      message.error(
        err?.response?.data?.message || t("Failed to convert currency"),
      );
    } finally {
      setLoading(false);
    }
  };

  const showPreview =
    credit && targetCurrency && targetCurrency !== sourceCurrency && exchangeRate && exchangeRate > 0;

  const convert = (val: number) =>
    (val * exchangeRate).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  return (
    <Modal
      open={open}
      title={
        <span>
          <SwapOutlined style={{ marginRight: 8 }} />
          {t("Convert credit currency")}
        </span>
      }
      onCancel={handleCancel}
      className="standardHeader standardFooter"
      onOk={() => form.submit()}
      okButtonProps={{ loading, icon: <SwapOutlined /> }}
      okText={t("Convert")}
      destroyOnClose
    >
      <Alert
        type="warning"
        message={t("This will permanently convert all amounts in this credit to the target currency.")}
        style={{ marginBottom: 16 }}
        showIcon
      />

      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item label={t("From currency")}>
          <Tag color="blue" style={{ fontSize: 14, padding: "4px 12px" }}>
            {sourceCurrency}
          </Tag>
        </Form.Item>

        <Form.Item
          name="targetCurrency"
          label={t("To currency")}
          rules={[{ required: true, message: t("Select target currency") }]}
        >
          <Select placeholder={t("Select currency")} style={{ width: 160 }}>
            {CURRENCIES.filter((c) => c !== sourceCurrency).map((c) => (
              <Select.Option key={c} value={c}>
                {c}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="exchangeRate"
          label={
            targetCurrency && targetCurrency !== sourceCurrency
              ? t("Exchange rate (1 {{from}} = ? {{to}})", {
                  from: sourceCurrency,
                  to: targetCurrency,
                })
              : t("Exchange rate")
          }
          rules={[
            { required: true, message: t("Enter exchange rate") },
            { type: "number", min: 0.000001, message: t("Rate must be greater than 0") },
          ]}
        >
          <InputNumber
            style={{ width: "100%" }}
            min={0.000001}
            step={0.01}
            placeholder="117.50"
            precision={6}
          />
        </Form.Item>
      </Form>

      {showPreview && credit && (
        <Descriptions
          title={t("Preview after conversion")}
          bordered
          size="small"
          column={1}
          style={{ marginTop: 8 }}
        >
          <Descriptions.Item label={t("Principal")}>
            <span style={{ color: "#888" }}>
              {credit.totalAmount?.toLocaleString()} {sourceCurrency}
            </span>
            {" → "}
            <strong>
              {convert(credit.totalAmount || 0)} {targetCurrency}
            </strong>
          </Descriptions.Item>
          <Descriptions.Item label={t("Total with interest")}>
            <span style={{ color: "#888" }}>
              {credit.totalWithInterest?.toLocaleString()} {sourceCurrency}
            </span>
            {" → "}
            <strong>
              {convert(credit.totalWithInterest || 0)} {targetCurrency}
            </strong>
          </Descriptions.Item>
          <Descriptions.Item label={t("Monthly rate")}>
            <span style={{ color: "#888" }}>
              {credit.monthlyAmount?.toLocaleString()} {sourceCurrency}
            </span>
            {" → "}
            <strong>
              {convert(credit.monthlyAmount || 0)} {targetCurrency}
            </strong>
          </Descriptions.Item>
          <Descriptions.Item label={t("Remaining")}>
            <span style={{ color: "#888" }}>
              {credit.remainingAmount?.toLocaleString()} {sourceCurrency}
            </span>
            {" → "}
            <strong>
              {convert(credit.remainingAmount || 0)} {targetCurrency}
            </strong>
          </Descriptions.Item>
        </Descriptions>
      )}
    </Modal>
  );
};

export default CreditCurrencyConvertModal;
